import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaCode, FaShoppingCart, FaMobile, FaDatabase, FaCloud, FaRocket, FaCheckCircle, FaReact, FaNodeJs, FaPython, FaWordpress, FaLaptopCode, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { SiNextdotjs, SiMongodb, SiPostgresql, SiTailwindcss } from 'react-icons/si';
import heroImage from '../../assets/076c6be09214b9f99db46e39bc93e550.jpg';

const WebDevelopmentPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const services = [
        { icon: FaShoppingCart, title: 'E-Commerce Websites', desc: 'Full-featured online stores with payment gateways, inventory management, and order tracking', image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600' },
        { icon: FaLaptopCode, title: 'Corporate Websites', desc: 'Professional business websites with CMS, blogs, and advanced functionality', image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600' },
        { icon: FaMobile, title: 'Responsive Web Apps', desc: 'Progressive web applications that work seamlessly across all devices', image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600' },
        { icon: FaDatabase, title: 'Custom Web Platforms', desc: 'Tailored web solutions with complex databases and business logic', image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600' },
        { icon: FaCloud, title: 'Cloud-Based Solutions', desc: 'Scalable web applications hosted on AWS, Azure, or Google Cloud', image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600' },
        { icon: FaRocket, title: 'Performance Optimization', desc: 'Lightning-fast websites with SEO optimization and CDN integration', image: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=600' }
    ];

    const technologies = [
        { name: 'React', icon: FaReact, desc: 'Modern UI library for interactive interfaces' },
        { name: 'Next.js', icon: SiNextdotjs, desc: 'Full-stack React framework with SSR' },
        { name: 'Node.js', icon: FaNodeJs, desc: 'JavaScript runtime for backend development' },
        { name: 'Python', icon: FaPython, desc: 'Django, Flask for robust backends' },
        { name: 'MongoDB', icon: SiMongodb, desc: 'NoSQL database for scalability' },
        { name: 'PostgreSQL', icon: SiPostgresql, desc: 'Reliable relational database' },
        { name: 'Tailwind CSS', icon: SiTailwindcss, desc: 'Utility-first CSS framework' },
        { name: 'WordPress', icon: FaWordpress, desc: 'CMS for content-rich websites' }
    ];

    const websiteTypes = [
        { title: 'E-Commerce Stores', examples: ['Online retail', 'Multi-vendor marketplaces', 'Booking platforms'] },
        { title: 'Corporate Websites', examples: ['Business portfolios', 'Company profiles', 'Service showcases'] },
        { title: 'Web Applications', examples: ['SaaS platforms', 'CRM systems', 'Project management tools'] },
        { title: 'Educational Platforms', examples: ['E-learning portals', 'Course management', 'Student tracking'] },
        { title: 'Blogs & News Sites', examples: ['Content platforms', 'Magazine websites', 'News portals'] },
        { title: 'Landing Pages', examples: ['Product launches', 'Campaign pages', 'Lead generation'] }
    ];

    const whyChooseUs = [
        { title: 'Expert Team', desc: 'Certified developers with 10+ years of experience in modern web technologies' },
        { title: 'Latest Technologies', desc: 'We use cutting-edge frameworks and tools to build future-ready websites' },
        { title: 'On-Time Delivery', desc: 'Strict project timelines with agile methodology for guaranteed delivery' },
        { title: 'Post-Launch Support', desc: '6 months free maintenance and 24/7 technical support after launch' },
        { title: 'SEO Optimized', desc: 'Every website is built with SEO best practices for better search rankings' },
        { title: 'Affordable Pricing', desc: 'Transparent pricing with no hidden costs. Best value for your investment' }
    ];

    const process = [
        { step: '01', title: 'Discovery & Planning', desc: 'Understanding your business goals and defining project scope' },
        { step: '02', title: 'Design & Prototype', desc: 'Creating wireframes and interactive prototypes for approval' },
        { step: '03', title: 'Development', desc: 'Building your website with clean, scalable, and secure code' },
        { step: '04', title: 'Testing & QA', desc: 'Rigorous testing across devices and browsers for quality assurance' },
        { step: '05', title: 'Launch & Deploy', desc: 'Going live with your website and ensuring smooth deployment' },
        { step: '06', title: 'Support & Maintenance', desc: 'Ongoing updates, security patches, and technical support' }
    ];

    const faqs = [
        { q: 'How long does it take to build a website?', a: 'A basic website takes 2-4 weeks, while complex web applications can take 8-12 weeks. Timeline depends on features and requirements.' },
        { q: 'What technologies do you use for web development?', a: 'We use modern frameworks like React, Next.js, Node.js, and databases like MongoDB and PostgreSQL. We choose the best tech stack for your specific needs.' },
        { q: 'Do you provide website maintenance after launch?', a: 'Yes! We provide 6 months of free maintenance including bug fixes, security updates, and minor changes. Extended support plans are also available.' },
        { q: 'Will my website be mobile-friendly?', a: 'Absolutely! All our websites are built with a mobile-first approach and are fully responsive across all devices and screen sizes.' },
        { q: 'Can you help with SEO and digital marketing?', a: 'Yes! We build SEO-optimized websites and offer digital marketing services including SEO, content marketing, and paid advertising.' },
        { q: 'What is the cost of website development?', a: 'Costs vary based on complexity. A basic website starts from ₹25,000, while advanced e-commerce or web apps start from ₹1,50,000. Contact us for a detailed quote.' }
    ];

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    return (
        <div className="bg-white font-sans">
            {/* Hero Section with Local Image - Reduced Height, Bottom Position */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-bottom"
                    style={{ backgroundImage: `url(${heroImage})` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-purple-900/80 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-2xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-blue-300 mb-3">What We Do &gt; Digital Business</p>
                        <h1 className="text-4xl md:text-5xl font-light mb-4 leading-tight">Web Development</h1>
                        <p className="text-base font-light text-gray-200 leading-relaxed mb-6">
                            Building powerful, scalable websites that drive business growth
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a href="/contact" className="px-5 py-2 bg-white text-blue-600 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                                Get Free Consultation
                            </a>
                            <a href="#services" className="px-5 py-2 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-blue-600 transition-all text-sm whitespace-nowrap">
                                Explore Services
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* What We Do Section */}
            <section className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">How We Build Websites</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            We follow a proven process combining modern development practices, agile methodology, and cutting-edge technologies to deliver websites that not only look great but perform exceptionally.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 mt-16">
                        {process.slice(0, 3).map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="text-center"
                            >
                                <div className="text-6xl font-bold text-blue-200 mb-4">{item.step}</div>
                                <h3 className="text-xl font-medium text-slate-900 mb-3">{item.title}</h3>
                                <p className="text-slate-600">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 mt-8">
                        {process.slice(3).map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="text-center"
                            >
                                <div className="text-6xl font-bold text-blue-200 mb-4">{item.step}</div>
                                <h3 className="text-xl font-medium text-slate-900 mb-3">{item.title}</h3>
                                <p className="text-slate-600">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technologies We Use */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Technologies We Use</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            We leverage the most powerful and modern technologies trusted by companies like Google, Facebook, Netflix, and Airbnb
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-6">
                        {technologies.map((tech, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.05 }}
                                className="p-6 bg-slate-50 rounded-sm border border-slate-200 text-center hover:shadow-lg transition-all group"
                            >
                                <tech.icon className="text-5xl mx-auto mb-4 text-blue-600 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-medium text-slate-900 mb-2">{tech.name}</h3>
                                <p className="text-sm text-slate-600">{tech.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Types of Websites We Build */}
            <section id="services" className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Types of Websites We Build</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            From simple landing pages to complex web applications, we build all types of websites tailored to your business needs
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="group relative h-64 overflow-hidden rounded-sm border border-slate-200 cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110" style={{ backgroundImage: `url(${service.image})` }}>
                                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/80"></div>
                                </div>
                                <div className="absolute inset-0 p-6 flex flex-col justify-between text-white z-10">
                                    <div>
                                        <service.icon className="text-4xl mb-4" />
                                        <h3 className="text-xl font-medium mb-2">{service.title}</h3>
                                    </div>
                                    <motion.div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-600 to-indigo-600 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                                        <p className="text-white text-sm leading-relaxed">{service.desc}</p>
                                        <button className="mt-4 text-white font-medium hover:underline">Learn More →</button>
                                    </motion.div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-20 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light mb-6">Why Choose Tech Inovant?</h2>
                        <p className="text-lg text-blue-100 max-w-3xl mx-auto">
                            We're not just another web development company. Here's what makes us different
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {whyChooseUs.map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-white/10 backdrop-blur-sm p-6 rounded-sm border border-white/20 hover:bg-white/20 transition-all"
                            >
                                <FaCheckCircle className="text-3xl text-green-400 mb-4" />
                                <h3 className="text-xl font-medium mb-3">{item.title}</h3>
                                <p className="text-blue-100 leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-20 bg-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Frequently Asked Questions</h2>
                        <p className="text-lg text-slate-600">Get answers to common questions about our web development services</p>
                    </div>

                    <div className="space-y-4">
                        {faqs.map((faq, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.05 }}
                                className="border border-slate-200 rounded-sm overflow-hidden"
                            >
                                <button
                                    onClick={() => toggleFaq(idx)}
                                    className="w-full px-6 py-4 text-left flex justify-between items-center bg-slate-50 hover:bg-slate-100 transition-colors"
                                >
                                    <span className="font-medium text-slate-900">{faq.q}</span>
                                    {openFaq === idx ? <FaChevronUp className="text-blue-600" /> : <FaChevronDown className="text-slate-400" />}
                                </button>
                                {openFaq === idx && (
                                    <div className="px-6 py-4 bg-white">
                                        <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
                    <h2 className="text-4xl md:text-5xl font-light mb-6">Ready to Build Your Dream Website?</h2>
                    <p className="text-xl font-light mb-8 text-blue-100">
                        Let's transform your vision into a powerful web presence that drives results
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <a href="/contact" className="inline-block px-6 py-2.5 bg-white text-blue-600 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                            Get Free Consultation
                        </a>
                        <a href="tel:+919876543210" className="inline-block px-6 py-2.5 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-blue-600 transition-all text-sm whitespace-nowrap">
                            Call Us Now
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default WebDevelopmentPage;
